import { useMemo, useCallback } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation } from '../../hooks';
import { queryCache } from '../../cache';
import { GradeService } from './service';
import {
  Grade,
  CreateGradeRequest,
  UpdateGradeRequest,
  GradeQueryParams,
  GradeQueryResponse,
} from './types';
import {
  useMergeSuggestions as useGenericMergeSuggestions,
  useMergePreview as useGenericMergePreview,
  useMerge as useGenericMerge,
} from '../merge/hooks';
import { GRADES_MERGE_CONFIG } from '../merge/config';

const CACHE_KEY = 'grades';

export function useGrades(params?: GradeQueryParams) {
  const api = useApi();
  const service = useMemo(() => new GradeService(api), [api]);
  const fetcher = useCallback(() => service.getAll(params), [service, params]);

  return useQuery<GradeQueryResponse>(CACHE_KEY, fetcher);
}

export function useGrade(id: number) {
  const api = useApi();
  const service = useMemo(() => new GradeService(api), [api]);
  const fetcher = useCallback(() => service.getById(id), [service, id]);

  return useQuery<Grade>(`${CACHE_KEY}/${id}`, fetcher);
}

export function useCreateGrade() {
  const api = useApi();
  const service = useMemo(() => new GradeService(api), [api]);

  return useMutation<Grade, CreateGradeRequest>(data => service.create(data), {
    onSuccess: () => {
      queryCache.invalidate(CACHE_KEY);
    },
  });
}

export function useUpdateGrade() {
  const api = useApi();
  const service = useMemo(() => new GradeService(api), [api]);

  return useMutation<Grade, UpdateGradeRequest>(data => service.update(data), {
    onSuccess: data => {
      queryCache.invalidate(CACHE_KEY);
      queryCache.invalidate(`${CACHE_KEY}/${data.grade_id}`);
    },
  });
}

export function useDeleteGrade() {
  const api = useApi();
  const service = useMemo(() => new GradeService(api), [api]);

  return useMutation<void, number>(id => service.delete(id), {
    onSuccess: (_, id) => {
      queryCache.invalidate(CACHE_KEY);
      queryCache.invalidate(`${CACHE_KEY}/${id}`);
    },
  });
}

// Convenience hook that combines all grades operations
export function useGradesPage(params?: GradeQueryParams) {
  const gradeResponse = useGrades(params);
  const createGrade = useCreateGrade();
  const updateGrade = useUpdateGrade();
  const deleteGrade = useDeleteGrade();

  return useMemo(
    () => ({
      // Query operations
      grades: gradeResponse,

      // Mutation operations
      create: createGrade,
      update: updateGrade,
      delete: deleteGrade,

      // Convenience getters for common states
      data:
        gradeResponse.status === 'success'
          ? (gradeResponse.data.grades ?? [])
          : [],
      total:
        gradeResponse.status === 'success'
          ? (gradeResponse.data?.total ?? 0)
          : 0,
      isLoading: gradeResponse.status === 'loading',
      isError: gradeResponse.status === 'error',
      error: gradeResponse.status === 'error' ? gradeResponse.error : null,

      // Actions
      refetch: gradeResponse.refetch,
      invalidate: gradeResponse.invalidate,
    }),
    [gradeResponse, createGrade, updateGrade, deleteGrade]
  );
}

// ============================================================================
// MERGE HOOKS - Using generic merge functionality
// ============================================================================

/**
 * Get merge suggestions for a grade
 */
export function useMergeSuggestions(sourceId: number | null, maxResults = 10) {
  return useGenericMergeSuggestions<Grade>(
    GRADES_MERGE_CONFIG,
    sourceId,
    maxResults
  );
}

/**
 * Get merge preview showing impact of merging two grades
 */
export function useMergePreview(
  sourceId: number | null,
  targetId: number | null
) {
  return useGenericMergePreview(GRADES_MERGE_CONFIG, sourceId, targetId);
}

/**
 * Execute a grade merge operation
 */
export function useMergeGrades() {
  return useGenericMerge(GRADES_MERGE_CONFIG, CACHE_KEY);
}
